import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import tokenStore from '../utils/tokenStore';
import { API_URL } from '../constants/api';

const TeamContext = createContext();

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within TeamProvider');
  }
  return context;
};

export const TeamProvider = ({ children }) => {
  const [teamMembers, setTeamMembers] = useState([{ id: '', name: 'Unassigned' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = tokenStore.get();
      if (!token) return;

      const response = await authenticatedFetch(`${API_URL}/api/team/members`, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const members = await response.json();
        setTeamMembers([
          { id: '', name: 'Unassigned' },
          ...members.map(m => ({ id: m.id, name: m.name, email: m.email, avatar: m.avatar }))
        ]);
      } else {
        console.error('Failed to fetch team members:', response.status);
        setError('Failed to fetch team members');
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const addTeamMember = useCallback(async (userId) => {
    const token = tokenStore.get();
    if (!token) throw new Error('No authentication token');

    const response = await authenticatedFetch(`${API_URL}/api/team/add/${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Add team member failed:', response.status, errorData);
      throw new Error(errorData.detail || 'Failed to add team member');
    }

    // Refresh list after successful add
    await fetchTeamMembers();
    return true;
  }, [fetchTeamMembers]);

  const removeTeamMember = useCallback(async (userId) => {
    const token = tokenStore.get();
    if (!token) throw new Error('No authentication token');

    const response = await authenticatedFetch(`${API_URL}/api/team/remove/${userId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Remove team member failed:', response.status, errorData);
      throw new Error(errorData.detail || 'Failed to remove team member');
    }

    // Refresh list after successful remove
    await fetchTeamMembers();
    return true;
  }, [fetchTeamMembers]);

  const resetTeamMembers = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) throw new Error('No authentication token');

    const response = await authenticatedFetch(`${API_URL}/api/team/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Failed to reset team members');
    }

    // Refresh list after reset
    await fetchTeamMembers();
    return true;
  }, [fetchTeamMembers]);

  const value = useMemo(() => ({
    teamMembers,
    loading,
    error,
    fetchTeamMembers,
    addTeamMember,
    removeTeamMember,
    resetTeamMembers
  }), [teamMembers, loading, error, fetchTeamMembers, addTeamMember, removeTeamMember, resetTeamMembers]);

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};
